import { useEffect, useRef, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { ClienteFormValues } from "../ClienteFormSchema";
import { FormattedTextField } from "@/components/form/FormattedTextField";
import { DateField } from "@/components/form/DateField";
import {
  PipelineFormSection,
  pipelineFieldGridClass,
  usePipelineForm,
} from "@/components/form/PipelineFormContext";
import { useCPFValidation } from "../../hooks/useCPFValidation";
import { useClienteTypeDetection } from "../../hooks/useClienteTypeDetection";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";

interface PersonalInfoStepProps {
  form: UseFormReturn<ClienteFormValues>;
  clienteId?: string;
  onSearchClick?: () => void;
  enableSearch?: boolean;
}

export function PersonalInfoStep({
  form,
  clienteId,
  onSearchClick,
  enableSearch,
}: PersonalInfoStepProps) {
  const { validateCPF, cpfExists, isValidating } = useCPFValidation({ clienteId });
  const { isPF, isPJ } = useClienteTypeDetection({ cpfCnpj: form.watch("cpf") });
  const { isPipeline } = usePipelineForm();
  const cpfValue = form.watch("cpf");
  const timeoutRef = useRef<NodeJS.Timeout>();

  const debouncedValidateCPF = useCallback(
    (cpf: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => validateCPF(cpf), 800);
    },
    [validateCPF]
  );

  useEffect(() => {
    if (cpfValue && (cpfValue.length >= 11 || cpfValue.length >= 14)) {
      debouncedValidateCPF(cpfValue);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [cpfValue, debouncedValidateCPF]);

  const gridClass = pipelineFieldGridClass(isPipeline);

  const cpfFeedback = cpfExists && !isValidating ? (
    <p className={`text-[11px] text-red-500 ${isPipeline ? 'px-3 pb-2' : 'mt-1'}`}>
      Este CPF/CNPJ já está cadastrado
    </p>
  ) : isValidating ? (
    <p className={`text-[11px] text-gray-500 ${isPipeline ? 'px-3 pb-2' : 'mt-1'}`}>
      Verificando CPF/CNPJ...
    </p>
  ) : null;

  return (
    <PipelineFormSection>
      <div className={isPipeline ? '' : 'space-y-8'}>
        <div className={gridClass}>
          <div className={enableSearch && !isPipeline ? 'relative' : undefined}>
            <FormattedTextField
              form={form}
              name="nome"
              label={isPJ ? "Nome Fantasia" : "Nome Completo"}
              formatter="texto"
              placeholder={isPJ ? "Nome fantasia da empresa" : "Nome completo"}
              required
            />
            {enableSearch && !isPipeline && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onSearchClick}
                className="absolute right-2 top-8 h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                title="Buscar cliente existente"
              >
                <Search className="h-4 w-4" />
              </Button>
            )}
          </div>

          {isPJ && (
            <>
              <FormattedTextField
                form={form}
                name="razaoSocial"
                label="Razão Social"
                formatter="texto"
                placeholder="Razão social da empresa"
                required={isPJ}
              />
              <FormattedTextField
                form={form}
                name="inscricaoEstadual"
                label="Inscrição Estadual"
                formatter="texto"
                placeholder="Inscrição estadual (se houver)"
              />
            </>
          )}

          <FormattedTextField
            form={form}
            name="email"
            label={isPJ ? "Email Corporativo" : "Email"}
            formatter="texto"
            placeholder="exemplo@email.com"
          />

          <FormattedTextField
            form={form}
            name="tel1"
            label={isPJ ? "Telefone Empresarial" : "Telefone Principal"}
            formatter="telefone"
            placeholder="(00) 00000-0000"
          />

          {isPF && (
            <FormattedTextField
              form={form}
              name="tel2"
              label="Telefone Secundário"
              formatter="telefone"
              placeholder="(00) 00000-0000"
            />
          )}

          <FormattedTextField
            form={form}
            name="cpf"
            label="CPF/CNPJ"
            formatter="cpfCnpj"
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
            required
          />
          {cpfFeedback}

          {isPF ? (
            <>
              <FormattedTextField
                form={form}
                name="rg"
                label="RG"
                formatter="texto"
                placeholder="00.000.000-0"
              />
              <DateField
                form={form}
                name="dataNascimento"
                label="Data de Nascimento"
                disableFuture
              />
              <DateField
                form={form}
                name="dataEmissaoRg"
                label="Data Emissão RG"
                disableFuture
              />
            </>
          ) : (
            <>
              <FormattedTextField
                form={form}
                name="nomeResponsavel"
                label="Nome do Responsável"
                formatter="texto"
                placeholder="Nome completo do responsável"
                required={isPJ}
              />
              <FormattedTextField
                form={form}
                name="cpfResponsavel"
                label="CPF do Responsável"
                formatter="cpf"
                placeholder="000.000.000-00"
                required={isPJ}
              />
              <FormattedTextField
                form={form}
                name="emailResponsavel"
                label="Email do Responsável"
                formatter="texto"
                placeholder="responsavel@email.com"
              />
              <FormattedTextField
                form={form}
                name="telResponsavel"
                label="Telefone do Responsável"
                formatter="telefone"
                placeholder="(00) 00000-0000"
              />
            </>
          )}
        </div>
      </div>
    </PipelineFormSection>
  );
}
